import React, { useState } from 'react';
import { SolicitacoesService } from '../services/solicitacoesService';
import { SolicitacaoAgendamento } from '../types';
import { api } from '../config/api';

interface LogUsuario {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_login: string;
  acao: string;
  modulo: string;
  descricao: string;
  dados_anteriores: any;
  dados_novos: any;
  data_acao: string;
  filial_atual: string;
}

interface AcaoHistorico {
  log: LogUsuario;
  detalhes: string;
  responsavel: string;
  data: string;
  tipo: string;
}

const RastreamentoSolicitacao: React.FC = () => {
  const [termoBusca, setTermoBusca] = useState<string>('');
  const [tipoBusca, setTipoBusca] = useState<'fornecedor' | 'id'>('fornecedor');
  const [resultados, setResultados] = useState<SolicitacaoAgendamento[]>([]);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState<SolicitacaoAgendamento | null>(null);
  const [historico, setHistorico] = useState<AcaoHistorico[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingHistorico, setLoadingHistorico] = useState<boolean>(false);
  const [erro, setErro] = useState<string>('');

  const buscarSolicitacoes = async () => {
    if (!termoBusca.trim()) {
      setErro('Digite um termo para buscar');
      return;
    }

    setLoading(true);
    setErro('');
    setResultados([]);
    setSolicitacaoSelecionada(null);
    setHistorico([]);

    try {
      if (tipoBusca === 'id') {
        const solicitacao = await SolicitacoesService.buscarPorId(termoBusca.trim());
        if (solicitacao) {
          setResultados([solicitacao]);
          selecionarSolicitacao(solicitacao);
        } else {
          setErro('Solicitação não encontrada');
        }
      } else {
        const lista = await SolicitacoesService.buscarPorFornecedor(termoBusca.trim());
        if (lista.length === 0) {
          setErro('Nenhuma solicitação encontrada para este fornecedor');
        } else {
          setResultados(lista);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar:', error);
      setErro('Erro ao buscar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const selecionarSolicitacao = async (solicitacao: SolicitacaoAgendamento) => {
    setSolicitacaoSelecionada(solicitacao);
    setLoadingHistorico(true);
    setHistorico([]);

    try {
      const response = await api.get(`/logs/solicitacao/${solicitacao.id}`);
      const logs: LogUsuario[] = Array.isArray(response.data) ? response.data : (response.data?.data || []);

      const historicoProcessado: AcaoHistorico[] = logs.map((log) => {
        let detalhes = '';
        let tipo = '';

        switch (log.acao) {
          case 'CRIAR':
            tipo = 'Criação';
            detalhes = log.descricao || 'Solicitação criada';
            break;
          case 'ATUALIZAR_STATUS':
            tipo = 'Atualização de Status';
            detalhes = log.descricao || 'Status atualizado';
            break;
          case 'APROVAR':
            tipo = 'Aprovação';
            detalhes = log.descricao || 'Solicitação aprovada';
            break;
          case 'REPROVAR':
            tipo = 'Reprovação';
            detalhes = log.descricao || 'Solicitação reprovada';
            break;
          case 'EXCLUIR':
            tipo = 'Exclusão';
            detalhes = log.descricao || 'Solicitação excluída';
            break;
          case 'ATUALIZAR_ORDEM_CHEGADA':
            tipo = 'Ordem de Chegada';
            detalhes = log.descricao || 'Ordem de chegada atualizada';
            break;
          case 'ATUALIZAR_STATUS_CARGA':
            tipo = 'Status da Carga';
            detalhes = log.descricao || 'Status da carga atualizado';
            break;
          case 'ATRIBUIR_CONFERENTE':
            tipo = 'Conferente';
            detalhes = log.descricao || 'Conferente atribuído';
            break;
          case 'REGISTRAR_OCORRENCIA':
            tipo = 'Ocorrência';
            detalhes = log.descricao || 'Ocorrência registrada';
            break;
          case 'REAGENDAR_CARGA':
            tipo = 'Reagendamento';
            detalhes = log.descricao || 'Carga reagendada';
            break;
          case 'UPLOAD_ARQUIVO':
            tipo = 'Documento';
            detalhes = log.descricao || 'Arquivo enviado';
            break;
          default:
            tipo = 'Ação';
            detalhes = log.descricao || log.acao;
        }

        return {
          log,
          detalhes,
          responsavel: log.usuario_nome || 'Sistema',
          data: log.data_acao,
          tipo
        };
      });

      setHistorico(historicoProcessado);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const formatarData = (data: string) => {
    if (!data) return '-';
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusCor = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s.includes('PENDENTE')) return 'bg-yellow-100 text-yellow-800';
    if (s.includes('APROVAD')) return 'bg-green-100 text-green-800';
    if (s.includes('REPROVAD')) return 'bg-red-100 text-red-800';
    if (s.includes('CANCELAD')) return 'bg-gray-100 text-gray-800';
    if (s.includes('FINALIZAD')) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getTipoCor = (tipo: string) => {
    const cores: Record<string, string> = {
      'Criação': 'bg-blue-100 text-blue-800',
      'Aprovação': 'bg-green-100 text-green-800',
      'Reprovação': 'bg-red-100 text-red-800',
      'Reagendamento': 'bg-yellow-100 text-yellow-800',
      'Documento': 'bg-purple-100 text-purple-800',
      'Comentário': 'bg-gray-100 text-gray-800',
      'Ordem de Chegada': 'bg-indigo-100 text-indigo-800',
      'Status da Carga': 'bg-cyan-100 text-cyan-800',
      'Conferente': 'bg-teal-100 text-teal-800',
      'Ocorrência': 'bg-orange-100 text-orange-800',
      'Exclusão': 'bg-red-100 text-red-800',
      'Atualização de Status': 'bg-amber-100 text-amber-800'
    };
    return cores[tipo] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Rastreamento de Solicitações</h1>

      {/* Busca */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2">
            <button
              onClick={() => setTipoBusca('fornecedor')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tipoBusca === 'fornecedor' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Por Fornecedor
            </button>
            <button
              onClick={() => setTipoBusca('id')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tipoBusca === 'id' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Por ID
            </button>
          </div>
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarSolicitacoes()}
              placeholder={tipoBusca === 'fornecedor' ? 'Digite o nome do fornecedor...' : 'Digite o ID da solicitação...'}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <button
              onClick={buscarSolicitacoes}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>

        {erro && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {erro}
          </div>
        )}
      </div>

      {/* Lista de resultados da busca */}
      {resultados.length > 0 && !solicitacaoSelecionada && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{resultados.length} solicitação(ões) encontrada(s)</h3>
          <div className="space-y-2">
            {resultados.map((sol) => (
              <button
                key={sol.id}
                onClick={() => selecionarSolicitacao(sol)}
                className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{sol.fornecedor}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Data: {sol.data_desejada || 'N/A'} | Filial: {sol.filial || 'N/A'} | Solicitante: {sol.solicitante_nome || 'N/A'}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusCor(sol.status)}`}>
                    {sol.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Solicitação selecionada + histórico */}
      {solicitacaoSelecionada && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-sm font-semibold text-gray-700">Informações da Solicitação</h3>
              <button
                onClick={() => { setSolicitacaoSelecionada(null); setHistorico([]); }}
                className="text-xs text-blue-600 hover:underline"
              >
                Voltar para lista
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-xs text-gray-500">Fornecedor</span>
                <p className="font-medium text-sm text-gray-900">{solicitacaoSelecionada.fornecedor}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Status</span>
                <p className="font-medium text-sm">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${getStatusCor(solicitacaoSelecionada.status)}`}>
                    {solicitacaoSelecionada.status}
                  </span>
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Data Desejada</span>
                <p className="font-medium text-sm text-gray-900">{solicitacaoSelecionada.data_desejada || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Filial</span>
                <p className="font-medium text-sm text-gray-900">{solicitacaoSelecionada.filial || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Solicitante</span>
                <p className="font-medium text-sm text-gray-900">{solicitacaoSelecionada.solicitante_nome || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Peso</span>
                <p className="font-medium text-sm text-gray-900">{solicitacaoSelecionada.peso || 0} kg</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Criado em</span>
                <p className="font-medium text-sm text-gray-900">{formatarData(solicitacaoSelecionada.data_solicitacao)}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">ID</span>
                <p className="font-medium text-xs text-gray-400 truncate">{solicitacaoSelecionada.id}</p>
              </div>
            </div>
          </div>

          {/* Timeline de histórico */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Linha do Tempo de Ações</h3>

            {loadingHistorico ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : historico.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Nenhuma ação registrada para esta solicitação</p>
            ) : (
              <div className="relative">
                {historico.map((acao, index) => (
                  <div key={index} className="flex gap-4 pb-6 last:pb-0">
                    {/* Linha vertical + marcador */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </div>
                      {index < historico.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-200 mt-1"></div>
                      )}
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoCor(acao.tipo)}`}>
                          {acao.tipo}
                        </span>
                        <span className="text-xs text-gray-400">{formatarData(acao.data)}</span>
                      </div>

                      <p className="text-sm text-gray-800 font-medium mb-1">{acao.detalhes}</p>

                      <div className="text-xs text-gray-500">
                        Responsavel: <span className="font-medium text-gray-700">{acao.responsavel}</span>
                        {acao.log.usuario_login && <span className="text-gray-400"> ({acao.log.usuario_login})</span>}
                      </div>

                      {acao.log.filial_atual && (
                        <div className="text-xs text-gray-400 mt-0.5">Filial: {acao.log.filial_atual}</div>
                      )}

                      {acao.log.dados_novos && (
                        <details className="mt-2">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:underline">Ver detalhes tecnicos</summary>
                          <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-40">{JSON.stringify(acao.log.dados_novos, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RastreamentoSolicitacao;
